import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  useSaveTagMutation,
  useSessionQuery,
  useSetTagActiveMutation,
  useTagsQuery,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";
import { datedFilename, downloadCsv } from "@/lib/csv";
import type { ManagedTag, TagInput, TagType } from "@/types";
import { TAG_TYPES } from "@/types";
import { AdminOnly } from "@/components/layout/AdminOnly";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  SortableHead,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from "@/components/ui/table";
import {
  Page,
  PageFill,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
  Rail,
} from "@/components/layout/Page";

export const Route = createFileRoute("/admin/tags")({
  component: TagsPage,
});

function TagsPage() {
  const session = useSessionQuery();
  return (
    <AdminOnly role={session.data?.user.role} minRole="manager">
      <TagsBody />
    </AdminOnly>
  );
}

type SortKey = "tag" | "documents" | "rank" | "type";

/** An empty form. `rank` is a string so the input can be genuinely blank. */
const emptyDraft = {
  tag: "",
  tagType: "Topic" as TagType,
  description: "",
  rank: "",
  notes: "",
  relatedTags: [] as string[],
};

type Draft = typeof emptyDraft;

function TagsBody() {
  const tags = useTagsQuery();
  const save = useSaveTagMutation();
  const setActive = useSetTagActiveMutation();
  const [search, setSearch] = useState("");
  const { sort, dir, headProps } = useSort<SortKey>("rank", "asc", { documents: "desc" });
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [relatedInput, setRelatedInput] = useState("");

  const all = useMemo(() => tags.data?.tags ?? [], [tags.data]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? all.filter(
          (t) =>
            t.tag.toLowerCase().includes(q) ||
            (t.description ?? "").toLowerCase().includes(q) ||
            (t.notes ?? "").toLowerCase().includes(q),
        )
      : all;
    return [...filtered].sort((a, b) => {
      switch (sort.key) {
        case "tag":
          return a.tag.localeCompare(b.tag) * dir;
        case "type":
          return (a.tagType ?? "").localeCompare(b.tagType ?? "") * dir;
        case "documents":
          return (a.documents - b.documents) * dir;
        default:
          // An unranked tag sorts last in either direction, because "unranked"
          // is not "ranked zero" and putting it first in an ascending sort
          // would bury every rank somebody actually set.
          if (a.rank === b.rank) return a.tag.localeCompare(b.tag);
          if (a.rank === null) return 1;
          if (b.rank === null) return -1;
          return (a.rank - b.rank) * dir;
      }
    });
  }, [all, search, sort, dir]);

  const orphaned = rows.filter((t) => t.orphaned);
  const unused = rows.filter((t) => !t.orphaned && t.documents === 0);
  const vocabulary = useMemo(
    () => all.filter((t) => !t.orphaned).map((t) => t.tag),
    [all],
  );

  const startEdit = (t: ManagedTag) => {
    setEditingId(t.id);
    setDraft({
      tag: t.tag,
      tagType: t.tagType ?? "Topic",
      description: t.description ?? "",
      rank: t.rank !== null ? String(t.rank) : "",
      notes: t.notes ?? "",
      relatedTags: t.relatedTags ?? [],
    });
    setRelatedInput("");
    // The form is a rail that is always on screen, so there is nowhere to
    // scroll to; focusing the field it opens on is the equivalent gesture and
    // it also tells a keyboard user that something happened.
    document.getElementById("tag-name")?.focus();
  };

  const reset = () => {
    setEditingId(null);
    setDraft(emptyDraft);
    setRelatedInput("");
    save.reset();
  };

  // A tag retired while its form is open would otherwise be saved back as
  // active, because the upsert restores by default.
  useEffect(() => {
    if (editingId && !all.some((t) => t.id === editingId)) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, editingId]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = draft.tag.trim();
    if (!name) return;
    const input: TagInput = {
      tag: name,
      tagType: draft.tagType,
      description: draft.description.trim(),
      notes: draft.notes.trim(),
      relatedTags: draft.relatedTags,
    };
    // An empty rank field means "leave it alone", not "set it to zero" — so
    // the key is omitted rather than sent as a number the server would store.
    const rank = Number(draft.rank);
    if (draft.rank.trim() !== "" && Number.isFinite(rank)) input.rank = rank;
    save.mutate(input, { onSuccess: reset });
  };

  const addRelated = (value: string) => {
    const v = value.trim();
    if (!v || v === draft.tag || draft.relatedTags.includes(v)) return;
    setDraft((d) => ({ ...d, relatedTags: [...d.relatedTags, v] }));
    setRelatedInput("");
  };

  return (
    <Page>
      <PageHeader
        title="Tags"
        description="The topic vocabulary the library filters and the Ask page’s filters are built from."
      />

      {/*
        The vocabulary is the page and the editor is a tool for changing it, so
        the editor goes beside the list rather than on top of it. Stacked, the
        form was roughly four hundred pixels of controls between the heading
        and the tags — and clicking Edit on a row scrolled the list out of
        sight to reach it, so you could not see the tag you were editing.
      */}
      <PageFill className="gap-4 xl:grid xl:min-h-0 xl:grid-cols-[minmax(0,1fr)_25rem] xl:items-stretch">
        <div className="flex min-h-0 flex-col gap-3">
        {/* The two states worth acting on, called out above the table rather
            than left for the reader to spot in a long list. An orphaned tag is
            the one ERIKA accumulated silently: the document processor invents a
            topic, it lands on documents, and nobody ever curates it. */}
        {(orphaned.length > 0 || unused.length > 0) && (
          <div className="grid shrink-0 gap-3 sm:grid-cols-2">
            {orphaned.length > 0 && (
              <div className="rounded-card border border-amber/40 bg-amber-bg p-4 text-xs text-amber-text">
                <p className="font-medium">
                  {orphaned.length} tag{orphaned.length === 1 ? "" : "s"} on documents but not in the
                  vocabulary
                </p>
                <p className="mt-1">
                  The document processor wrote these. Add them to make them curatable, or leave them
                  — filtering works either way.
                </p>
              </div>
            )}
            {unused.length > 0 && (
              <div className="rounded-card border border-border bg-card p-4 text-xs text-muted-foreground">
                <p className="font-medium text-foreground">
                  {unused.length} tag{unused.length === 1 ? "" : "s"} on no documents
                </p>
                <p className="mt-1">
                  These offer a filter that matches nothing. Retiring one hides it without touching
                  any document.
                </p>
              </div>
            )}
          </div>
        )}

        <Panel className="min-h-[24rem] xl:min-h-0">
          <PanelHeader>
            <PanelTitle>{rows.length} tags</PanelTitle>
            <div className="flex items-center gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search names, descriptions, notes"
                className="h-8 w-48 text-xs lg:w-64"
                aria-label="Search tags"
              />
            <Button
              size="sm"
              variant="secondary"
              className="text-xs"
              disabled={tags.isFetching}
              onClick={() => void tags.refetch()}
            >
              {tags.isFetching ? "Refreshing…" : "Refresh"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="text-xs"
              disabled={rows.length === 0}
              onClick={() =>
                downloadCsv(
                  datedFilename("tags"),
                  ["tag", "type", "rank", "documents", "state", "description", "related", "notes"],
                  rows.map((t) => [
                    t.tag,
                    t.tagType,
                    t.rank,
                    t.documents,
                    t.orphaned ? "not in vocabulary" : t.active ? "active" : "retired",
                    t.description ?? "",
                    (t.relatedTags ?? []).join("; "),
                    t.notes ?? "",
                  ]),
                )
              }
            >
              Export CSV
            </Button>
            </div>
          </PanelHeader>

          <PanelBody>
            {tags.isLoading && <p className="p-4 text-sm text-muted-foreground">Loading…</p>}
            {tags.isError && (
              <p className="p-4 text-sm text-muted-foreground">Tags could not be loaded.</p>
            )}

            {tags.data && (
              <Table className="min-w-[44rem]">
                <TableHeader>
                  <TableRow>
                    <SortableHead {...headProps("tag", "Tag")} className="min-w-[16rem]" />
                    <SortableHead {...headProps("type", "Type")} className="w-32" />
                    <SortableHead {...headProps("rank", "Rank")} align="right" className="w-24" />
                    <SortableHead {...headProps("documents", "Documents")} align="right" className="w-32" />
                    <TableHead className="w-40">State</TableHead>
                    <TableHead className="w-52 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((t) => (
                    <TableRow
                      key={t.orphaned ? `orphan:${t.tag}` : t.id}
                      className={t.active ? "" : "opacity-60"}
                    >
                      <TableCell>
                        {t.documents > 0 ? (
                          // A tag's real definition is the documents carrying
                          // it, so the name is a link to exactly those.
                          <Link
                            to="/library"
                            search={{ tags: [t.tag] }}
                            className="text-primary hover:underline"
                          >
                            {t.tag}
                          </Link>
                        ) : (
                          <span>{t.tag}</span>
                        )}
                        {t.description && (
                          <p className="mt-0.5 max-w-prose text-xs text-muted-foreground">
                            {t.description}
                          </p>
                        )}
                        {t.relatedTags && t.relatedTags.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {t.relatedTags.map((r) => (
                              <span
                                key={r}
                                className="rounded-full border border-input px-1.5 py-0.5 text-[10px] text-muted-foreground"
                              >
                                {r}
                              </span>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {t.orphaned ? "—" : t.tagType}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                        {t.rank ?? "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs tabular-nums">{t.documents}</TableCell>
                      <TableCell>
                        {t.orphaned ? (
                          <Badge tone="amber">Not in vocabulary</Badge>
                        ) : t.active ? (
                          <Badge tone="teal">Active</Badge>
                        ) : (
                          <Badge tone="gray">Retired</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {t.orphaned ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="text-xs"
                            disabled={save.isPending}
                            onClick={() => save.mutate({ tag: t.tag })}
                          >
                            Add to vocabulary
                          </Button>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              className="text-xs"
                              onClick={() => startEdit(t)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="text-xs"
                              disabled={setActive.isPending}
                              onClick={() => {
                                // Retiring takes a filter away from everyone
                                // in the organisation, and the number of
                                // documents it covers is what makes that
                                // concrete — so the confirmation says it.
                                if (
                                  t.active &&
                                  !window.confirm(
                                    `Retire “${t.tag}”?\n\n` +
                                      `It disappears from the Ask page and library filters. ` +
                                      `${t.documents} document${t.documents === 1 ? "" : "s"} keep the tag, ` +
                                      `and restoring it brings the filter back.`,
                                  )
                                ) {
                                  return;
                                }
                                setActive.mutate({ id: t.id, active: !t.active });
                              }}
                            >
                              {t.active ? "Retire" : "Restore"}
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableEmpty
                      colSpan={6}
                      message={search ? "No tag matches that search." : "No tags yet."}
                    />
                  )}
                </TableBody>
              </Table>
            )}
          </PanelBody>
        </Panel>
        </div>

        {/* The rail scrolls on its own; the table beside it scrolls on its
            own. Two regions, side by side, so a wheel over either one has
            exactly one thing it can move. */}
        <Rail className="shrink-0 overflow-y-auto overscroll-contain pb-1 xl:min-h-0">
          {/* Add and edit are one form, because the vocabulary is keyed on the
            name someone types: typing a name that already exists is an edit, and
            making the page ask first would be a round trip to guard against a
            case that is not an error. */}
          <Card>
          <CardHeader>
            <CardTitle>{editingId ? `Editing “${draft.tag}”` : "Add or update a tag"}</CardTitle>
            {editingId && (
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={reset}>
                Cancel
              </button>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              <div>
                <label className="mb-1 block text-xs font-medium" htmlFor="tag-name">
                  Name
                </label>
                <Input
                  id="tag-name"
                  value={draft.tag}
                  onChange={(e) => setDraft((d) => ({ ...d, tag: e.target.value }))}
                  placeholder="Retention"
                />
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-medium" htmlFor="tag-type">
                    Type
                  </label>
                  <Select
                    id="tag-type"
                    className="w-full"
                    value={draft.tagType}
                    onChange={(e) => setDraft((d) => ({ ...d, tagType: e.target.value as TagType }))}
                  >
                    {TAG_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="w-24">
                  <label className="mb-1 block text-xs font-medium" htmlFor="tag-rank">
                    Rank
                  </label>
                  <Input
                    id="tag-rank"
                    inputMode="numeric"
                    value={draft.rank}
                    onChange={(e) => setDraft((d) => ({ ...d, rank: e.target.value }))}
                    placeholder="—"
                  />
                </div>
              </div>

              <div className="sm:col-span-2 xl:col-span-1">
                <label className="mb-1 block text-xs font-medium" htmlFor="tag-description">
                  Description
                </label>
                <Textarea
                  id="tag-description"
                  rows={2}
                  value={draft.description}
                  onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  placeholder="What this tag covers, in a sentence."
                />
                {/* Not decorative. ERIKA fed this to the model when deciding
                    which documents get the tag, so the sentence written here
                    changes what the tag comes to mean over time. */}
                <p className="mt-1 text-xs text-muted-foreground">
                  Read by the model when deciding which documents carry this tag — write it as a
                  definition, not a note to yourself.
                </p>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium" htmlFor="tag-related">
                  Related tags
                </label>
                <div className="flex gap-2">
                  <Input
                    id="tag-related"
                    list="tag-vocabulary"
                    value={relatedInput}
                    onChange={(e) => setRelatedInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        // Otherwise Enter submits the form and saves a tag
                        // nobody was finished writing.
                        e.preventDefault();
                        addRelated(relatedInput);
                      }
                    }}
                    placeholder="Add a related tag"
                  />
                  <datalist id="tag-vocabulary">
                    {vocabulary.map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="text-xs"
                    onClick={() => addRelated(relatedInput)}
                  >
                    Add
                  </Button>
                </div>
                {draft.relatedTags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {draft.relatedTags.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className="rounded-full border border-input px-2 py-0.5 text-xs hover:border-danger hover:text-danger-text"
                        title="Remove"
                        onClick={() =>
                          setDraft((d) => ({ ...d, relatedTags: d.relatedTags.filter((x) => x !== t) }))
                        }
                      >
                        {t} ×
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium" htmlFor="tag-notes">
                  Notes
                </label>
                <Textarea
                  id="tag-notes"
                  rows={2}
                  value={draft.notes}
                  onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                  placeholder="For librarians. Not read by the model."
                />
              </div>

              <div className="flex items-center justify-between gap-3 sm:col-span-2 xl:col-span-1">
                <span className="text-xs text-danger-text">
                  {save.isError &&
                    (save.error instanceof ApiError ? save.error.message : "That tag could not be saved.")}
                </span>
                <Button
                  type="submit"
                  size="sm"
                  variant="primary"
                  className="text-xs"
                  disabled={save.isPending || draft.tag.trim() === ""}
                >
                  {save.isPending ? "Saving…" : editingId ? "Save changes" : "Add tag"}
                </Button>
              </div>
            </form>
          </CardContent>
          </Card>

          {/* Retiring is not deleting, and the difference matters enough to
              say. On a card rather than straight onto the backdrop: the
              gradient is near-white by the time it reaches the bottom of the
              rail, and white text on it cannot be read. */}
          <p className="rounded-card border border-border bg-card p-3 text-xs text-muted-foreground shadow-panel">
            Retiring a tag hides it from the filters. Documents already carrying it keep it, and
            restoring the tag brings the filter back.
          </p>
        </Rail>
      </PageFill>
    </Page>
  );
}
